import React, { useState, useEffect } from 'react';

import { generateBreadcrumbs, type BreadcrumbItem } from './breadcrumbLogic';

interface BreadcrumbsProps {
  breadcrumbs?: BreadcrumbItem[];
}

const HomeIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 15 14"
    fill="none"
    aria-label="Homepage"
    className="stroke-current w-4 h-4"
  >
    <title>Homepage</title>
    <path d="M13.841 6.617 7.883.66a.542.542 0 0 0-.766 0L1.16 6.617a.542.542 0 0 0 .383.925h.677c.075 0 .135.06.135.135v5.281c0 .3.243.542.542.542h3.115c.074 0 .135-.06.135-.135v-2.573a1.354 1.354 0 1 1 2.708 0v2.573c0 .074.06.135.136.135h3.114c.3 0 .542-.242.542-.542v-5.28c0-.076.06-.136.135-.136h.678a.542.542 0 0 0 .382-.925Z" />
  </svg>
);

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  breadcrumbs: propBreadcrumbs,
}) => {
  // Initialize with server-provided breadcrumbs from Redux store
  // Fallback to client-side generation if props not provided or empty
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[] | null>(
    () => {
      // Check if we have valid breadcrumbs (array with items)
      if (propBreadcrumbs && propBreadcrumbs.length > 0) {
        return propBreadcrumbs;
      }

      // Fallback: generate from URL on client-side when server fails or returns empty
      if (typeof window !== 'undefined') {
        const currentLocation =
          window.location.pathname + window.location.search;
        return generateBreadcrumbs(currentLocation);
      }

      return null;
    }
  );

  useEffect(() => {
    // Handle client-side URL changes for dynamic breadcrumb updates
    const handleLocationChange = () => {
      const newPath = window.location.pathname + window.location.search;
      const newBreadcrumbs = generateBreadcrumbs(newPath);
      setBreadcrumbs(newBreadcrumbs);
    };

    // Listen for browser back/forward button navigation
    window.addEventListener('popstate', handleLocationChange);

    // Intercept client-side navigation (e.g., category changes within search)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleLocationChange();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      handleLocationChange();
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null;
  }

  const isHome = (title: string) => /^home$/i.test(title);

  return (
    <nav
      className="breadcrumbs bg-white flex py-2 h-12"
      aria-label="Breadcrumb"
    >
      <div className="inline-flex items-center space-x-1 md:space-x-3 pl-4 lg:pl-8 overflow-hidden py-2 overflow-x-scroll scrollbar-hidden whitespace-nowrap">
        {breadcrumbs.map((breadcrumb, index) => (
          <div
            className="flex items-center"
            key={`${breadcrumb.title}-${index}`}
          >
            {index > 0 && (
              <svg
                className="fill-current text-gray-600 w-2 h-2"
                viewBox="0 0 24 24"
              >
                <path d="M19.5 12a2.34 2.34 0 0 1-.78 1.73L7.57 23.54a1.85 1.85 0 1 1-2.44-2.77l9.75-8.58a.25.25 0 0 0 0-.38L5.13 3.23A1.85 1.85 0 1 1 7.57.46l11.15 9.81A2.34 2.34 0 0 1 19.5 12Z" />
              </svg>
            )}
            {isHome(breadcrumb.title) ? (
              <a
                href={breadcrumb.path}
                className="inline-flex items-center text-gray-600 hover:text-blue-800"
              >
                <HomeIcon />
              </a>
            ) : breadcrumb.path && index < breadcrumbs.length - 1 ? (
              <a
                href={breadcrumb.path}
                className="ml-1 md:ml-2 text-sm font-normal text-gray-600 no-underline hover:text-blue-800"
              >
                {breadcrumb.title}
              </a>
            ) : (
              <div className="ml-1 md:ml-2 text-sm font-normal text-gray-600 no-underline">
                {breadcrumb.title}
              </div>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
};

export default Breadcrumbs;
